import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { FormField } from "../components/ui/FormField/FormField";
import { Select } from "../components/ui/Select/Select";
import { Modal } from "../components/ui/Modal/Modal";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { useLeadList, useCreateLead, type Lead } from "../hooks/use-leads";
import { useSapCountries, useSapStates } from "../hooks/use-companies";
import { isValidPhone } from "../lib/phone";
import { useAuth } from "../lib/use-auth";
import { hasPermission } from "../lib/permissions";
import styles from "./Leads.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/leads",
	component: withPermission(LeadsPage, "leads", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
	}),
});

const EMPTY_LEAD_FORM = {
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	companyName: "",
	tradeName: "",
	taxNumber: "",
	taxOffice: "",
	fax: "",
	website: "",
	industry: "",
	addressStreet: "",
	addressCity: "",
	addressState: "",
	addressPostalCode: "",
	addressCountry: "Türkiye",
};

function LeadsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: "/leads" });
	const { user } = useAuth();
	const canUpdate = hasPermission(user, "leads", "update");
	const { page, size, status, search: searchQuery, createdAtFrom, createdAtTo } = route.useSearch();
	const setStatus = (s: string) =>
		navigate({ search: (prev) => ({ ...prev, status: s, page: 1 }) });
	const setPage = (p: number) => navigate({ search: (prev) => ({ ...prev, page: p }) });
	const setPageSize = (s: number) =>
		navigate({ search: (prev) => ({ ...prev, size: s, page: 1 }) });
	const [createOpen, setCreateOpen] = useState(false);
	const [form, setForm] = useState(EMPTY_LEAD_FORM);
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const { data, isLoading, isError, refetch } = useLeadList({
		status: status || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: size,
	});
	const createLead = useCreateLead();
	const { data: countries } = useSapCountries();
	const selectedCountryCode = countries?.find((c) => c.name === form.addressCountry)?.code;
	const { data: states } = useSapStates(selectedCountryCode);

	const leads = data?.items ?? [];
	const total = data?.total ?? 0;

	const tabs = [
		{ key: "", label: t("all") },
		{ key: "new", label: t("new") },
		{ key: "converted", label: t("converted") },
	];

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.firstName?.trim()) errors.firstName = t("requiredField");
		if (!form.lastName?.trim()) errors.lastName = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		if (form.phone && !isValidPhone(form.phone)) {
			setFormError(t("invalidPhone"));
			return;
		}
		const { addressStreet, addressCity, addressState, addressPostalCode, addressCountry, ...rest } =
			form;
		const payload = {
			...(Object.fromEntries(
				Object.entries(rest)
					.map(([k, v]) => [k, v.trim()])
					.filter(([, v]) => v !== ""),
			) as typeof rest),
			address:
				addressStreet || addressCity || addressState || addressPostalCode
					? {
							street: addressStreet.trim() || undefined,
							city: addressCity.trim() || undefined,
							state: addressState.trim() || undefined,
							postalCode: addressPostalCode.trim() || undefined,
							country: addressCountry.trim() || undefined,
						}
					: undefined,
		};
		createLead.mutate(payload, {
			onSuccess: () => {
				setCreateOpen(false);
				setForm(EMPTY_LEAD_FORM);
				setFormError("");
				alert(t("createSuccess"));
			},
			onError: (err) => {
				setFormError(err instanceof Error ? err.message : t("createFailed"));
			},
		});
	};

	return (
		<div>
			<PageHeader
				title={t("leads")}
				action={<Button onClick={() => setCreateOpen(true)}>{t("createLead")}</Button>}
			/>

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{tabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								setStatus(tab.key);
								setPage(1);
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>
			<FilterBar
				filters={[
					{ key: "search", type: "text", label: t("search") },
					{ key: "createdAt", type: "dateRange", label: t("createdAt") },
				]}
				values={{ search: searchQuery, createdAtFrom, createdAtTo }}
				onChange={(vals) => {
					navigate({
						search: (prev) => ({
							...prev,
							search: vals.search || "",
							createdAtFrom: vals.createdAtFrom || "",
							createdAtTo: vals.createdAtTo || "",
							page: 1,
						}),
					});
				}}
			/>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{
								key: "name",
								header: t("firstName"),
								render: (row) => `${row.firstName} ${row.lastName}`,
							},
							{ key: "companyName", header: t("companyName") },
							{ key: "email", header: t("email") },
							{ key: "phone", header: t("phone") },
							{
								key: "status",
								header: t("status"),
								render: (row) => <StatusBadge status={row.status} type="lead" />,
							},
							...(canUpdate
								? [
										{
											key: "owner",
											header: t("salesPerson"),
											render: (row: Lead) =>
												row.owner
													? [row.owner.firstName, row.owner.lastName].filter(Boolean).join(" ")
													: "—",
										},
									]
								: []),
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/leads/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={leads}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={size}
					total={total}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</Card>

			<Modal
				isOpen={createOpen}
				onClose={() => setCreateOpen(false)}
				title={t("createLead")}
				closeOnOverlayClick={false}
			>
				{formError && <div className={styles.formError}>{formError}</div>}
				<FormField label={t("firstName")} required>
					<Input
						value={form.firstName}
						onChange={(e) => setForm({ ...form, firstName: e.target.value })}
						error={fieldErrors.firstName}
					/>
				</FormField>
				<FormField label={t("lastName")} required>
					<Input
						value={form.lastName}
						onChange={(e) => setForm({ ...form, lastName: e.target.value })}
						error={fieldErrors.lastName}
					/>
				</FormField>
				<FormField label={t("email")}>
					<Input
						type="email"
						value={form.email}
						onChange={(e) => setForm({ ...form, email: e.target.value })}
					/>
				</FormField>
				<FormField label={t("phone")}>
					<Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
				</FormField>
				<FormField label={t("companyName")}>
					<Input
						value={form.companyName}
						onChange={(e) => setForm({ ...form, companyName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("tradeName")}>
					<Input
						value={form.tradeName}
						onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
					/>
				</FormField>
				<FormField label="Adres">
					<Input
						value={form.addressStreet}
						onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
					/>
				</FormField>
				<FormField label="Posta Kodu">
					<Input
						value={form.addressPostalCode}
						onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })}
					/>
				</FormField>
				<FormField label="İlçe">
					<Input
						value={form.addressCity}
						onChange={(e) => setForm({ ...form, addressCity: e.target.value })}
					/>
				</FormField>
				<FormField label="İl">
					<Select
						value={form.addressState}
						onChange={(e) => setForm({ ...form, addressState: e.target.value })}
						options={[
							{ value: "", label: t("notAvailable") },
							...(states ?? []).map((s) => ({ value: s.name, label: s.name })),
						]}
					/>
				</FormField>
				<FormField label="Ülke">
					<Select
						value={form.addressCountry}
						onChange={(e) => {
							const country = e.target.value;
							setForm((prev) => {
								if (country && country !== "Türkiye") {
									return {
										...prev,
										addressCountry: country,
										addressState: "",
										taxNumber: prev.taxNumber.trim() || "2222222222",
									};
								}
								return {
									...prev,
									addressCountry: country,
									addressState: "",
									taxNumber: prev.taxNumber === "2222222222" ? "" : prev.taxNumber,
								};
							});
						}}
						options={[
							{ value: "", label: t("notAvailable") },
							...(countries ?? []).map((c) => ({ value: c.name, label: c.name })),
						]}
					/>
				</FormField>
				<FormField label={t("taxNumber")}>
					<Input
						value={form.taxNumber}
						onChange={(e) => setForm({ ...form, taxNumber: e.target.value })}
					/>
				</FormField>
				<FormField label={t("taxOffice")}>
					<Input
						value={form.taxOffice}
						onChange={(e) => setForm({ ...form, taxOffice: e.target.value })}
					/>
				</FormField>
				<FormField label={t("fax")}>
					<Input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} />
				</FormField>
				<FormField label={t("website")}>
					<Input
						value={form.website}
						onChange={(e) => setForm({ ...form, website: e.target.value })}
					/>
				</FormField>
				<FormField label={t("industry")}>
					<Input
						value={form.industry}
						onChange={(e) => setForm({ ...form, industry: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setCreateOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={submit} isLoading={createLead.isPending}>
						{t("create")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}
